import { useEffect, useState } from "react";
import { Table } from "antd";
import { isNull } from "lodash";
import { BASE_COLORS, FONT_WEIGHT } from "config/theme/constants";
import { SortOrder } from "antd/es/table/interface";
import { TableWrapper } from "./styles";
import { generateDynamicColumn, pageSizeOptions } from "./const";

interface TablePropsParams {
  columns: any;
  data: Array<any> | undefined;
  loading?: boolean;
  columnActions?: any;
  isPagination?: boolean;
  isNoStaticWidth?: boolean;
  initialData?: Array<any> | undefined;
  scrollXWidth?: number;
  onChange?: any;
  rowClassName?: string;
  totalCounts?: number;
  pagination?: {
    current: number;
    pageSize: number;
    total?: number;
  };
  sortField?: string;
  sortOrder?: string;
  isServerSidePagination?: boolean;
}

const DynamicTable = ({
  columns,
  data,
  loading,
  columnActions,
  isPagination = true,
  isNoStaticWidth,
  initialData,
  scrollXWidth,
  onChange,
  rowClassName,
  totalCounts = 0,
  pagination,
  sortField,
  sortOrder,
  isServerSidePagination = true,
}: TablePropsParams) => {
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: pagination?.current || 1,
      pageSize: pagination?.pageSize || 10,
      total: totalCounts,
    },
    sorter: {
      field: sortField || "",
      order:
        sortOrder === "DESC"
          ? "descend"
          : sortOrder === "ASC"
            ? "ascend"
            : null,
    },
  });

  useEffect(() => {
    if (isServerSidePagination) {
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          current: pagination?.current || 1,
          pageSize: pagination?.pageSize || 10,
          total: totalCounts,
        },
      }));
    }
  }, [pagination, totalCounts, isServerSidePagination]);

  useEffect(() => {
    if (isServerSidePagination) {
      setTableParams((prev) => ({
        ...prev,
        sorter: {
          field: sortField || "",
          order:
            sortOrder === "DESC"
              ? "descend"
              : sortOrder === "ASC"
                ? "ascend"
                : prev.sorter.order,
        },
      }));
    }
  }, [sortField, sortOrder, isServerSidePagination]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (isServerSidePagination) {
      const isSortingChanged =
        sorter.field !== tableParams.sorter.field ||
        sorter.order !== tableParams.sorter.order;

      let updatedSortOrder: SortOrder | null = tableParams.sorter
        .order as SortOrder;

      if (isSortingChanged) {
        if (
          sorter.field !== tableParams.sorter.field &&
          sorter.field !== undefined
        ) {
          updatedSortOrder =
            tableParams.sorter.order === "descend"
              ? "ascend"
              : tableParams.sorter.order === "ascend"
                ? "descend"
                : tableParams.sorter.order === null
                  ? "ascend"
                  : null;
        } else {
          updatedSortOrder =
            tableParams.sorter.order === "descend"
              ? null
              : tableParams.sorter.order === "ascend"
                ? "descend"
                : "ascend";
        }
      }

      const newParams = {
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          current: pagination.current,
          pageSize: pagination.pageSize,
        },
        sorter: isSortingChanged
          ? {
              field: sorter.field || tableParams.sorter.field,
              order: updatedSortOrder,
            }
          : tableParams.sorter,
      };
      document.querySelector(".ant-table-wrapper")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setTableParams(newParams);

      if (onChange) {
        onChange(
          {
            current: pagination.current,
            pageSize: pagination.pageSize,
          },
          filters,
          {
            ...sorter,
            order:
              updatedSortOrder === "descend"
                ? "DESC"
                : updatedSortOrder === "ascend"
                  ? "ASC"
                  : "",
          },
          newParams,
        );
      }
    } else {
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          current: pagination.current,
          pageSize: pagination.pageSize,
        },
        sorter: {
          field: sorter.field,
          order: sorter.order,
        },
      }));
    }
  };

  const processedData =
    !isServerSidePagination && data
      ? data.slice(
          (tableParams.pagination.current - 1) *
            tableParams.pagination.pageSize,
          tableParams.pagination.current * tableParams.pagination.pageSize,
        )
      : data;

  return (
    <TableWrapper>
      <Table
        columns={generateDynamicColumn(
          columns,
          columnActions,
          tableParams.sorter.order,
          tableParams.sorter.field,
        )}
        scroll={
          scrollXWidth
            ? { x: scrollXWidth }
            : isNoStaticWidth
              ? {}
              : { x: 1000 }
        }
        dataSource={processedData}
        pagination={
          isPagination
            ? {
                ...tableParams.pagination,
                total: isServerSidePagination ? totalCounts : data?.length || 0,
                pageSizeOptions,
                showSizeChanger: true,
                hideOnSinglePage: false,
                showTotal: (total, range) => (
                  <div
                    style={{
                      marginLeft:
                        tableParams.pagination.pageSize === 10 ? 0 : 5,
                      color: BASE_COLORS.black,
                      fontSize: FONT_WEIGHT.normal,
                    }}
                    className="showing-count-list"
                  >
                    {`Showing ${range[0]} to ${range[1]} of ${total} entries`}{" "}
                    {initialData?.length !== data?.length
                      ? `(filtered from ${initialData?.length} total entries)`
                      : ""}
                  </div>
                ),
                locale: { items_per_page: "" },
              }
            : false
        }
        loading={loading}
        onChange={handleTableChange}
        size="small"
        bordered
        showSorterTooltip={false}
        rowClassName={(record) =>
          !isNull(record?.releaseConfirm) && record?.releaseConfirm <= 0
            ? rowClassName || ""
            : ""
        }
      />
    </TableWrapper>
  );
};

export default DynamicTable;
